import { type ReactNode, createElement } from 'react';
import { WidgetElement, TUNNEL_HOST_STYLES } from '../core';
import { REDIRECT_RETURN_PARAM } from '@cfixe-monorepo/shared-components/pro-payment/redirect-return';
import { GiftCardRoot } from './mount-gift-card';
// Tunnel carte cadeau (réutilise les classes bt-* du tunnel de réservation) + UI paiement (pp-*).
// Bundlés inline par Vite (?inline renvoie une string), injectés dans le shadow root.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — handled by Vite plugin
import giftCardStyles from '@cfixe-monorepo/shared-components/booking/styles/booking-tunnel.scss?inline';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — handled by Vite plugin
import proPaymentStyles from '@cfixe-monorepo/shared-components/pro-payment/styles/pro-payment.scss?inline';

const ELEMENT_TAG = 'propelsia-gift-card';

/**
 * Custom Element du widget carte cadeau Propelsia (mirror de `<propelsia-booking>`).
 *
 * Attributs (en plus des attributs de thème standards de widget-core) :
 *  - widget-id (requis)  UUID public stable du pro (ou slug legacy)
 *  - slug                Fallback si widget-id absent
 *  - lang                Locale (défaut: fr-FR)
 *  - mode                'inline' (défaut) ou 'modal'
 *  - Thème               accent, bg, text, radius, font
 *
 * Events (bubble + composed) :
 *  - propelsia:gift-card:completed    detail: { voucherUuid }
 */
export class PropelsiaGiftCardElement extends WidgetElement {
  static get observedAttributes(): readonly string[] {
    return ['widget-id', 'slug', 'lang', 'mode', 'accent', 'bg', 'text', 'radius', 'font'];
  }

  protected override getModalTriggerLabel(): string {
    return 'Offrir une carte cadeau';
  }

  protected override getRedirectReturnParams(): readonly string[] {
    return [REDIRECT_RETURN_PARAM.GIFT_CARD];
  }

  protected override injectStyles(shadow: ShadowRoot): void {
    const style = document.createElement('style');
    style.textContent = `
      ${TUNNEL_HOST_STYLES}
      ${giftCardStyles as string}
      ${proPaymentStyles as string}
    `;
    shadow.appendChild(style);
  }

  protected override getRootComponent(): ReactNode {
    const identifier = this.getAttribute('widget-id') ?? this.getAttribute('slug') ?? '';
    if (!identifier) {
      return createElement(
        'div',
        { style: { padding: 16, color: '#c00' } },
        'Propelsia gift-card: missing required attribute "widget-id"',
      );
    }

    return createElement(GiftCardRoot, {
      slug: identifier,
      // Attribut `lang` de <propelsia-gift-card> → formatage montants/téléphone (défaut fr-FR côté tunnel).
      locale: this.getAttribute('lang') ?? undefined,
      handleRedirectReturn: this.ownsRedirectReturn,
      onCompleted: (voucherUuid: string): void => {
        this.emit('propelsia:gift-card:completed', { voucherUuid });
        if (this.getMode() === 'modal') {
          window.setTimeout((): void => this.closeModal(), 6000);
        }
      },
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get(ELEMENT_TAG)) {
  customElements.define(ELEMENT_TAG, PropelsiaGiftCardElement);
}
